/**
 * DeedsReviewPanel (Sprint M4)
 *
 * Deeds-specific HITL review surfaced inside the Data Validation stage when a document carries
 * Deeds Automation enrichment (parcel match / ownership flags / sale-coding recommendation).
 *
 * The clerk confirms or overrides each result; overrides are written through the existing
 * ValidationContext as edits to the synthetic fields (camaParcelId / ownershipFlags /
 * saleValidationCode) the backend wrote into the document field set. Because those fields are
 * real entries in `state.document.fields`, edits are tracked in the edit history AND captured
 * server-side by persistFieldCorrections (FIELD_CORRECTIONS_PERSISTED audit entry) on submit —
 * no separate persistence endpoint. Status is always conveyed as text + an icon (never color
 * alone) and every control has a visible, associated label.
 */

import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Tooltip from '@mui/material/Tooltip';
import Stack from '@mui/material/Stack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useValidation } from '../../contexts/ValidationContext';

// ── Types (frontend mirror of azure-functions/src/types/deeds.ts) ─────────────

interface ParcelCandidate {
  parcelId: string;
  address?: string;
  legalDescription?: string;
  score: number;
}
interface ParcelMatch {
  status: 'matched' | 'ambiguous' | 'no-match' | 'skipped';
  parcelId?: string;
  confidence: number;
  method: string;
  candidates: ParcelCandidate[];
  requiresReview: boolean;
  message?: string;
}
interface OwnershipFlags {
  flags: string[];
  detected: Array<{ flag: string; source: string; rationale?: string }>;
  rationale: string;
  requiresReview: boolean;
}
interface SaleCoding {
  recommendedCode: string;
  label?: string;
  confidence: number;
  rationale: string;
  matchedBy: string;
  candidates: Array<{ code: string; label: string }>;
  requiresReview: boolean;
}
export interface DeedsEnrichment {
  parcelMatch?: ParcelMatch;
  ownershipFlags?: OwnershipFlags;
  saleCoding?: SaleCoding;
  requiresReview: boolean;
}

// Synthetic field ids — must match DEEDS_SYNTHETIC_FIELDS in the backend (`field-{name}`).
const FIELD_ID = {
  parcelId: 'field-camaParcelId',
  flags: 'field-ownershipFlags',
  saleCode: 'field-saleValidationCode'
};

const ALL_FLAGS: Array<{ value: string; label: string }> = [
  { value: 'TRUST', label: 'Trust' },
  { value: 'JTWROS', label: 'Joint Tenants w/ Survivorship' },
  { value: 'TENANTS_IN_COMMON', label: 'Tenants in Common' },
  { value: 'LIFE_ESTATE', label: 'Life Estate' },
  { value: 'MULTIPARCEL', label: 'Multiparcel' },
  { value: 'AKA_FKA', label: 'AKA / FKA' }
];
const flagLabel = (v: string) => ALL_FLAGS.find(f => f.value === v)?.label ?? v;

/** Status chip with text + icon (never color alone) for a parcel-match outcome. */
function ParcelStatusChip({ status }: { status: ParcelMatch['status'] }) {
  const map: Record<ParcelMatch['status'], { label: string; color: 'success' | 'warning' | 'error' | 'default'; icon: React.ReactElement }> = {
    matched: { label: 'Matched', color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
    ambiguous: { label: 'Needs review', color: 'warning', icon: <WarningAmberIcon fontSize="small" /> },
    'no-match': { label: 'No match', color: 'error', icon: <ErrorOutlineIcon fontSize="small" /> },
    skipped: { label: 'Skipped', color: 'default', icon: <HelpOutlineIcon fontSize="small" /> }
  };
  const m = map[status];
  return <Chip size="small" variant="outlined" color={m.color} icon={m.icon} label={m.label} />;
}

interface DeedsReviewPanelProps {
  deeds: DeedsEnrichment;
}

const DeedsReviewPanel: React.FC<DeedsReviewPanelProps> = ({ deeds }) => {
  const { state, actions } = useValidation();
  const { parcelMatch, ownershipFlags, saleCoding } = deeds;

  const parcelValue = state.validatedFields[FIELD_ID.parcelId] ?? parcelMatch?.parcelId ?? '';
  const saleValue = state.validatedFields[FIELD_ID.saleCode] ?? saleCoding?.recommendedCode ?? '';
  const selectedFlags = useMemo(() => {
    const raw = state.validatedFields[FIELD_ID.flags] ?? (ownershipFlags?.flags || []).join(', ');
    return raw.split(',').map(s => s.trim()).filter(Boolean);
  }, [state.validatedFields, ownershipFlags]);

  if (!parcelMatch && !ownershipFlags && !saleCoding) return null;

  return (
    <Box sx={{ mb: 3 }} aria-label="Deeds review">
      <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 0.5 }}>
        Deeds Review
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Confirm the parcel match, ownership flags, and sale-validation code before approval.
      </Typography>

      <Stack spacing={2}>
        {/* ── Parcel match ── */}
        {parcelMatch && (
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600 }}>Parcel Match</Typography>
                <ParcelStatusChip status={parcelMatch.status} />
                {parcelMatch.method !== 'none' && (
                  <Chip size="small" variant="outlined" label={`by ${parcelMatch.method}`} />
                )}
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Confidence ${Math.round(parcelMatch.confidence * 100)}%`}
                />
              </Box>

              {parcelMatch.message && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {parcelMatch.message}
                </Typography>
              )}

              {parcelMatch.candidates.length > 0 && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="parcel-candidate-label">Matched parcel candidate</InputLabel>
                  <Select
                    labelId="parcel-candidate-label"
                    label="Matched parcel candidate"
                    value={parcelMatch.candidates.some(c => c.parcelId === parcelValue) ? parcelValue : ''}
                    onChange={(e) => actions.updateField(FIELD_ID.parcelId, e.target.value)}
                  >
                    {parcelMatch.candidates.map((c) => (
                      <MenuItem key={c.parcelId} value={c.parcelId}>
                        {c.parcelId}
                        {c.address ? ` — ${c.address}` : ''} ({Math.round(c.score * 100)}%)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <TextField
                fullWidth
                label="CAMA Parcel ID"
                value={parcelValue}
                onChange={(e) => actions.updateField(FIELD_ID.parcelId, e.target.value)}
                helperText={
                  parcelMatch.candidates.length > 0
                    ? 'Selecting a candidate above fills this field — or edit here to enter a parcel not listed'
                    : 'Enter the correct CAMA parcel ID'
                }
              />
            </CardContent>
          </Card>
        )}

        {/* ── Ownership / estate flags ── */}
        {ownershipFlags && (
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600, mb: 1 }}>
                Ownership &amp; Estate Flags
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, minHeight: 32 }}>
                {selectedFlags.length === 0 ? (
                  <Chip size="small" variant="outlined" icon={<CheckCircleIcon fontSize="small" />} label="No flags" />
                ) : (
                  selectedFlags.map((f) => {
                    const detail = ownershipFlags.detected.find(d => d.flag === f);
                    const chip = (
                      <Chip key={f} size="small" color="warning" variant="outlined"
                        icon={<WarningAmberIcon fontSize="small" />} label={flagLabel(f)} />
                    );
                    return detail?.rationale
                      ? <Tooltip key={f} title={detail.rationale} arrow>{chip}</Tooltip>
                      : chip;
                  })
                )}
              </Box>

              <FormControl fullWidth>
                <InputLabel id="flags-select-label">Flags</InputLabel>
                <Select
                  labelId="flags-select-label"
                  multiple
                  value={selectedFlags}
                  input={<OutlinedInput label="Flags" />}
                  onChange={(e) => {
                    const v = e.target.value;
                    const arr = typeof v === 'string' ? v.split(',') : v;
                    actions.updateField(FIELD_ID.flags, arr.join(', '));
                  }}
                  renderValue={(selected) => (selected as string[]).map(flagLabel).join(', ') || 'None'}
                >
                  {ALL_FLAGS.map((f) => (
                    <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </CardContent>
          </Card>
        )}

        {/* ── Sale coding ── */}
        {saleCoding && (
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600 }}>Sale Validation Code</Typography>
                {(() => {
                  // Three states: a confident criteria match (Recommended), an intentional
                  // config default (Default applied — neutral, not alarming), or no match (Needs review).
                  const s = saleCoding.matchedBy === 'criteria'
                    ? { color: 'success' as const, icon: <CheckCircleIcon fontSize="small" />, label: 'Recommended' }
                    : saleCoding.matchedBy === 'default'
                      ? { color: 'info' as const, icon: <HelpOutlineIcon fontSize="small" />, label: 'Default applied' }
                      : { color: 'warning' as const, icon: <WarningAmberIcon fontSize="small" />, label: 'Needs review' };
                  return <Chip size="small" variant="outlined" color={s.color} icon={s.icon} label={s.label} />;
                })()}
                <Chip size="small" variant="outlined" label={`Confidence ${Math.round(saleCoding.confidence * 100)}%`} />
              </Box>

              {saleCoding.rationale && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {saleCoding.rationale}
                </Typography>
              )}

              <FormControl fullWidth>
                <InputLabel id="sale-code-label">Sale code</InputLabel>
                <Select
                  labelId="sale-code-label"
                  label="Sale code"
                  value={saleValue}
                  onChange={(e) => actions.updateField(FIELD_ID.saleCode, e.target.value)}
                >
                  {/* Surface a stored value not in the table (e.g. a prior override) rather than
                      silently blanking the control. */}
                  {saleValue && !saleCoding.candidates.some(c => c.code === saleValue) && (
                    <MenuItem value={saleValue}>{saleValue} — (current)</MenuItem>
                  )}
                  {saleCoding.candidates.map((c) => (
                    <MenuItem key={c.code} value={c.code}>{c.code} — {c.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </CardContent>
          </Card>
        )}
      </Stack>
    </Box>
  );
};

export default DeedsReviewPanel;
